"use client"

import type React from "react"

import { createContext, useContext, useEffect, useState } from "react"

export interface WishlistItem {
  id: number
  name: string
  price: number
  originalPrice: number
  image: string
  category: string
}

interface WishlistContextType {
  items: WishlistItem[]
  addToWishlist: (item: WishlistItem) => void
  removeFromWishlist: (id: number) => void
  isInWishlist: (id: number) => boolean
  clearWishlist: () => void
  totalItems: number
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined)

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<WishlistItem[]>([])
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const savedWishlist = localStorage.getItem("luxury-jeff-wishlist")
    if (savedWishlist) {
      setItems(JSON.parse(savedWishlist))
    }
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted) {
      localStorage.setItem("luxury-jeff-wishlist", JSON.stringify(items))
    }
  }, [items, mounted])

  const addToWishlist = (newItem: WishlistItem) => {
    setItems((prev) => {
      const exists = prev.find((item) => item.id === newItem.id)
      if (exists) return prev
      return [...prev, newItem]
    })
  }

  const removeFromWishlist = (id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }

  const isInWishlist = (id: number) => {
    return items.some((item) => item.id === id)
  }

  const clearWishlist = () => {
    setItems([])
  }

  const totalItems = items.length

  if (!mounted) {
    return null
  }

  return (
    <WishlistContext.Provider
      value={{
        items,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        totalItems,
      }}
    >
      {children}
    </WishlistContext.Provider>
  )
}

export function useWishlist() {
  const context = useContext(WishlistContext)
  if (context === undefined) {
    throw new Error("useWishlist must be used within a WishlistProvider")
  }
  return context
}
